import type { CSSProperties } from 'react';
import { initials } from '../lib/when';

/**
 * A person, as a face would sit in a register: their initials on a colour that
 * is theirs.
 *
 * The colour comes from the name, so Aziza is the same colour on every screen
 * and every visit — a teacher learns to find her by it, the way they would
 * find her seat in the room. Ten hues, chosen to sit together, washed light so
 * a column of them reads as people rather than as a chart.
 */
const HUES = [8, 26, 42, 88, 148, 172, 198, 220, 258, 322];

export function hueFor(name: string): number {
  let hash = 0;
  for (const ch of name.trim().toLowerCase()) hash = (hash * 31 + (ch.codePointAt(0) ?? 0)) >>> 0;
  return HUES[hash % HUES.length]!;
}

export function Avatar({ name, size = 'md' }: { name: string; size?: 'sm' | 'md' | 'lg' }) {
  return (
    <span
      className={`avatar avatar--${size}`}
      style={{ '--hue': hueFor(name) } as CSSProperties}
      aria-hidden="true"
    >
      {initials(name)}
    </span>
  );
}

/** A name with its face beside it — the way a person appears in a list. */
export function Person({ name, detail }: { name: string; detail?: string | null }) {
  return (
    <span className="person">
      <Avatar name={name} size="sm" />
      <span className="person__text">
        <span className="person__name">{name}</span>
        {detail && <span className="person__detail">{detail}</span>}
      </span>
    </span>
  );
}
